import type { Track } from "../api.js";
import { audioContext, ensureRunning } from "./context.js";
import {
  LoadCancelled,
  loadTrackBuffer,
  type LoadProgress,
  type SegmentRequest,
} from "./load.js";

export type PlayerState = "empty" | "loading" | "ready" | "playing" | "error";

/**
 * Rate changes are ramped over this long. Short enough that a nudge still
 * feels immediate, long enough that the resampler does not click.
 */
const RATE_RAMP_SECONDS = 0.02;

export interface PlayerSnapshot {
  state: PlayerState;
  track: Track | null;
  position: number;
  duration: number;
  rate: number;
  progress: LoadProgress | null;
  error: string | null;
}

/**
 * Playback of one decoded track.
 *
 * Position is derived from the AudioContext clock rather than integrated per
 * frame: an AudioBufferSourceNode exposes no position, and accumulating
 * frame deltas would drift. Every rate change re-anchors the offset, so
 * position stays exact across tempo moves - which is the whole reason for
 * holding a decoded buffer (P9).
 */
export class TrackPlayer {
  readonly output: GainNode;

  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  private track: Track | null = null;
  private state: PlayerState = "empty";
  private error: string | null = null;
  private progress: LoadProgress | null = null;

  /** AudioContext time at which the current source started. */
  private anchorTime = 0;
  /** Buffer position corresponding to anchorTime. */
  private anchorOffset = 0;
  private rate = 1;
  /** Distinguishes a natural end from a stop() we caused. */
  private generation = 0;
  private loading: AbortController | null = null;

  constructor(private readonly onChange: (snapshot: PlayerSnapshot) => void) {
    this.output = audioContext().createGain();
  }

  snapshot(): PlayerSnapshot {
    return {
      state: this.state,
      track: this.track,
      position: this.position(),
      duration: this.buffer?.duration ?? this.track?.duration ?? 0,
      rate: this.rate,
      progress: this.progress,
      error: this.error,
    };
  }

  position(): number {
    if (!this.buffer) return 0;
    if (this.state !== "playing") return this.anchorOffset;
    const elapsed = (audioContext().currentTime - this.anchorTime) * this.rate;
    // Negative before a scheduled start; clamp so the UI shows the start offset
    // rather than counting up to it.
    return Math.min(this.anchorOffset + Math.max(0, elapsed), this.buffer.duration);
  }

  private emit(): void {
    this.onChange(this.snapshot());
  }

  async load(track: Track, segment?: SegmentRequest): Promise<void> {
    this.loading?.abort();
    this.stopSource();
    // Drop the previous buffer before allocating the next, so two long tracks
    // are never resident at once (C3).
    this.buffer = null;
    this.anchorOffset = 0;

    const controller = new AbortController();
    this.loading = controller;
    this.track = track;
    this.state = "loading";
    this.error = null;
    this.progress = { downloaded: 0, decoding: false };
    this.emit();

    try {
      const buffer = await loadTrackBuffer(
        track.id,
        controller.signal,
        (p) => {
          this.progress = p;
          this.emit();
        },
        segment,
      );
      if (controller.signal.aborted) return;
      this.buffer = buffer;
      this.state = "ready";
      this.progress = null;
      this.emit();
    } catch (err) {
      if (err instanceof LoadCancelled || controller.signal.aborted) return;
      this.state = "error";
      this.error = err instanceof Error ? err.message : String(err);
      this.progress = null;
      this.emit();
    } finally {
      if (this.loading === controller) this.loading = null;
    }
  }

  /**
   * Start at a specific audio-clock time, from a specific offset.
   *
   * Auto-mix schedules the whole transition ahead of time, because a
   * backgrounded tab cannot be relied on to run JS at the right moment (C7).
   */
  async playAt(when: number, offset: number): Promise<void> {
    if (!this.buffer) return;
    await ensureRunning();
    if (!this.buffer) return;
    this.stopSource();
    this.startSource(Math.max(0, Math.min(offset, this.buffer.duration)), when);
    this.state = "playing";
    this.emit();
  }

  async play(): Promise<void> {
    if (!this.buffer || this.state === "playing") return;
    await ensureRunning();
    if (!this.buffer) return;

    if (this.anchorOffset >= this.buffer.duration) this.anchorOffset = 0;
    this.startSource(this.anchorOffset);
    this.state = "playing";
    this.emit();
  }

  pause(): void {
    if (this.state !== "playing") return;
    this.anchorOffset = this.position();
    this.stopSource();
    this.state = "ready";
    this.emit();
  }

  async toggle(): Promise<void> {
    if (this.state === "playing") this.pause();
    else await this.play();
  }

  seek(seconds: number): void {
    if (!this.buffer) return;
    const target = Math.max(0, Math.min(seconds, this.buffer.duration));
    if (this.state === "playing") {
      this.stopSource();
      this.startSource(target);
    } else {
      this.anchorOffset = target;
    }
    this.emit();
  }

  /** Playback rate as a multiplier. Varispeed: pitch moves with tempo (C1). */
  setRate(rate: number): void {
    const next = Math.max(0.1, Math.min(rate, 4));
    if (next === this.rate) return;

    if (this.state === "playing") {
      // Re-anchor before the rate changes, or elapsed time would be scaled by
      // the new rate all the way back to the old anchor.
      this.anchorOffset = this.position();
      this.anchorTime = audioContext().currentTime;
    }
    this.rate = next;
    if (this.source) {
      // Ramped, not stepped (M5). A hard jump in playbackRate is a
      // discontinuity in the resampling ratio, which is audible as a click or
      // a tearing sound - most noticeable on nudge, where the rate moves by
      // several percent in one go.
      //
      // The position math anchors as though the new rate applied instantly.
      // The resulting error is bounded by half the rate delta times the ramp,
      // well under a millisecond for any realistic nudge, so it is not worth
      // integrating the ramp to correct.
      const param = this.source.playbackRate;
      const now = audioContext().currentTime;
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(next, now + RATE_RAMP_SECONDS);
    }
    this.emit();
  }

  getRate(): number {
    return this.rate;
  }

  /**
   * Ramp the playback rate over a scheduled window, for automated tempo bends
   * beyond what the tempo fader can express.
   *
   * The position anchor is moved to the end of the ramp and credited with the
   * audio consumed during it — the integral of a linear rate ramp, which is
   * simply the mean rate times its duration. Anchoring any other way makes
   * position wrong for the rest of the track.
   */
  rampRate(target: number, startAt: number, duration: number): void {
    if (!this.source || this.state !== "playing") {
      this.setRate(target);
      return;
    }
    const ctx = audioContext();
    const when = Math.max(startAt, ctx.currentTime);
    const next = Math.max(0.1, Math.min(target, 4));

    const param = this.source.playbackRate;
    param.cancelScheduledValues(ctx.currentTime);
    param.setValueAtTime(this.rate, when);
    param.linearRampToValueAtTime(next, when + duration);

    const positionAtRampStart = this.anchorOffset + (when - this.anchorTime) * this.rate;
    this.anchorOffset = positionAtRampStart + ((this.rate + next) / 2) * duration;
    this.anchorTime = when + duration;
    this.rate = next;
    this.emit();
  }

  /**
   * Play a slice of the loaded buffer at a scheduled time, alongside whatever
   * else is running. Used for loop rolls, where each repeat is its own source
   * because shortening a real loop would need JavaScript at every bar (X13b).
   */
  scheduleSlice(when: number, offset: number, duration: number): void {
    const buffer = this.buffer;
    if (!buffer || duration <= 0) return;
    const ctx = audioContext();

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this.rate;
    // A short fade at each end, or every repeat begins and ends with a click.
    const shaper = ctx.createGain();
    const edge = Math.min(0.004, duration / 4);
    const at = Math.max(when, ctx.currentTime);
    shaper.gain.setValueAtTime(0, at);
    shaper.gain.linearRampToValueAtTime(1, at + edge);
    shaper.gain.setValueAtTime(1, at + duration - edge);
    shaper.gain.linearRampToValueAtTime(0, at + duration);

    source.connect(shaper).connect(this.output);
    source.start(at, Math.max(0, Math.min(offset, buffer.duration)), duration);
    source.onended = () => {
      source.disconnect();
      shaper.disconnect();
    };
  }

  /** Silence the main source at a scheduled time, leaving slices audible. */
  stopAt(when: number): void {
    if (!this.source) return;
    try {
      this.source.stop(Math.max(when, audioContext().currentTime));
    } catch {
      /* already stopped */
    }
  }

  unload(): void {
    this.loading?.abort();
    this.loading = null;
    this.stopSource();
    this.buffer = null;
    this.track = null;
    this.anchorOffset = 0;
    this.state = "empty";
    this.progress = null;
    this.error = null;
    this.emit();
  }

  private startSource(offset: number, when?: number): void {
    const buffer = this.buffer;
    if (!buffer) return;
    const ctx = audioContext();
    const startTime = when === undefined ? 0 : Math.max(when, ctx.currentTime);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this.rate;
    source.connect(this.output);

    const generation = ++this.generation;
    source.onended = () => {
      // Ignore the ended event from a source we replaced or stopped.
      if (generation !== this.generation) return;
      this.state = "ready";
      this.anchorOffset = buffer.duration;
      this.source = null;
      this.emit();
    };

    source.start(startTime, offset);
    this.source = source;
    // Anchor on the scheduled start, not on now, so position stays correct for
    // a source that has not begun playing yet.
    this.anchorTime = when === undefined ? ctx.currentTime : startTime;
    this.anchorOffset = offset;
  }

  private stopSource(): void {
    if (!this.source) return;
    this.generation++;
    this.source.onended = null;
    try {
      this.source.stop();
    } catch {
      /* already stopped */
    }
    this.source.disconnect();
    this.source = null;
  }
}
